import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, brl, fileToBase64, sortProducts } from "@/api";
import { Money, HideMoneyButton } from "@/contexts/HiddenValues";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Edit, Search, X, Star, ChevronLeft, ChevronRight, Image as ImageIcon, Sparkles, Share2, ScanLine, Download } from "lucide-react";
import { toast } from "sonner";
import CurrencyInput from "@/components/CurrencyInput";
import SubtitleSelector from "@/components/SubtitleSelector";
import { readableTextColor } from "@/utils/contrast";
import BarcodeScanner from "@/components/BarcodeScanner";
import { useAuth } from "@/AuthContext";
import { ProductsExportDialog } from "@/components/ReportExportDialogs";

// ─── Canvas renderer 1080×1920 (Stories) ─────────────────────────────────────
// Desenha com Canvas API direto — NÃO usa html2canvas. Garante que palavras
// não fiquem grudadas (medimos cada palavra individualmente) e que descendentes
// (g, ç, p) não sejam cortados (line-height controlado manualmente).
const STORY_W = 1080;
const STORY_H = 1920;
const STORY_PHOTO_H = 950;
const STORY_PAD_X = 80;
const STORY_FONT_STACK = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) return reject(new Error("no src"));
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawImageCover(ctx, img, x, y, w, h) {
  const ar = img.width / img.height;
  const tar = w / h;
  let sw, sh, sx, sy;
  if (ar > tar) {
    sh = img.height;
    sw = img.height * tar;
    sx = (img.width - sw) / 2;
    sy = 0;
  } else {
    sw = img.width;
    sh = img.width / tar;
    sx = 0;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

// Quebra texto em linhas medindo cada palavra individualmente.
// Retorna { lines: [...], lineHeight }
function wrapTextLines(ctx, text, maxWidth, lineHeight) {
  const lines = [];
  const paragraphs = String(text || "").split(/\n+/);
  for (const para of paragraphs) {
    const words = para.split(/\s+/).filter(Boolean);
    if (words.length === 0) { lines.push(""); continue; }
    let line = words[0];
    for (let i = 1; i < words.length; i++) {
      const test = `${line} ${words[i]}`;
      if (ctx.measureText(test).width > maxWidth) {
        lines.push(line);
        line = words[i];
      } else {
        line = test;
      }
    }
    lines.push(line);
  }
  return { lines, lineHeight };
}

function drawWrappedText(ctx, text, x, y, maxWidth, fontSize, lineHeight, color) {
  ctx.fillStyle = color;
  const { lines } = wrapTextLines(ctx, text, maxWidth, lineHeight);
  for (const line of lines) {
    ctx.fillText(line, x, y);
    y += lineHeight;
  }
  return y;
}

async function renderProductStoryCanvas({ photoSrc, productName, categoryName, storeName, price, originalPrice, description }) {
  const canvas = document.createElement("canvas");
  canvas.width = STORY_W;
  canvas.height = STORY_H;
  const ctx = canvas.getContext("2d");
  ctx.textBaseline = "top";
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Fundo branco
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, STORY_W, STORY_H);

  // Foto (1080x950) com cover crop centralizado
  if (photoSrc) {
    try {
      const img = await loadImage(photoSrc);
      drawImageCover(ctx, img, 0, 0, STORY_W, STORY_PHOTO_H);
    } catch {
      ctx.fillStyle = "#F5F5F5";
      ctx.fillRect(0, 0, STORY_W, STORY_PHOTO_H);
    }
  } else {
    ctx.fillStyle = "#F5F5F5";
    ctx.fillRect(0, 0, STORY_W, STORY_PHOTO_H);
  }

  const maxTextWidth = STORY_W - STORY_PAD_X * 2;
  let y = STORY_PHOTO_H + 70;

  // Categoria
  if (categoryName) {
    ctx.font = `500 26px ${STORY_FONT_STACK}`;
    ctx.fillStyle = "#888888";
    ctx.fillText(categoryName.toUpperCase(), STORY_PAD_X, y);
    y += 26 + 22;
  }

  // Nome
  ctx.font = `700 64px ${STORY_FONT_STACK}`;
  y = drawWrappedText(ctx, productName, STORY_PAD_X, y, maxTextWidth, 64, Math.round(64 * 1.4), "#0A0A0A");
  y += 24;

  // Preço (com strike-through opcional do preço original)
  let priceX = STORY_PAD_X;
  if (originalPrice != null) {
    ctx.font = `400 34px ${STORY_FONT_STACK}`;
    ctx.fillStyle = "#999999";
    const origText = brl(originalPrice);
    const origMetrics = ctx.measureText(origText);
    ctx.fillText(origText, priceX, y + 30); // baseline aprox alinhada com preço grande
    // strike
    ctx.strokeStyle = "#999999";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(priceX, y + 30 + 18);
    ctx.lineTo(priceX + origMetrics.width, y + 30 + 18);
    ctx.stroke();
    priceX += origMetrics.width + 22;
  }
  ctx.font = `800 96px ${STORY_FONT_STACK}`;
  ctx.fillStyle = originalPrice != null ? "#047857" : "#0A0A0A";
  ctx.fillText(brl(price), priceX, y);
  y += Math.round(96 * 1.25);
  y += 18;

  // Descrição (multiparágrafos)
  // Estratégia anti-corte: ao invés de truncar quando excede maxY, reduz a fonte
  // automaticamente até a descrição inteira caber no espaço disponível.
  // Range: 28px (padrão) → 14px (mínimo legível). Step de 2px.
  if (description && description.trim()) {
    const paragraphs = description.split(/\n+/).map(s => s.trim()).filter(Boolean);
    const footerReserved = 130; // espaço para o rodapé
    const maxY = STORY_H - footerReserved;
    const availableH = Math.max(0, maxY - y);

    // Encontra a maior fonte (passo de 2px, de 28 a 14) onde TODOS os parágrafos cabem
    let chosen = { fontSize: 14, lineHeight: Math.round(14 * 1.65), totalH: 0, perPara: [] };
    for (let fs = 28; fs >= 14; fs -= 2) {
      ctx.font = `400 ${fs}px ${STORY_FONT_STACK}`;
      const lh = Math.round(fs * 1.65);
      let totalH = 0;
      const perPara = [];
      for (let i = 0; i < paragraphs.length; i++) {
        const { lines } = wrapTextLines(ctx, paragraphs[i], maxTextWidth, lh);
        perPara.push(lines);
        totalH += lines.length * lh;
        if (i < paragraphs.length - 1) totalH += 12; // espaçamento entre parágrafos
      }
      if (totalH <= availableH) {
        chosen = { fontSize: fs, lineHeight: lh, totalH, perPara };
        break;
      }
      // Mesmo na fonte mínima (14), aceita — melhor exibir tudo com fonte menor
      // do que cortar; se não couber em 14, vai apenas até onde der.
      if (fs === 14) {
        chosen = { fontSize: fs, lineHeight: lh, totalH, perPara };
      }
    }

    ctx.font = `400 ${chosen.fontSize}px ${STORY_FONT_STACK}`;
    ctx.fillStyle = "#444444";
    for (let i = 0; i < chosen.perPara.length; i++) {
      for (const line of chosen.perPara[i]) {
        // Última defesa: se mesmo na fonte 14 não couber, para — evita
        // sobrescrever o rodapé. Caso raríssimo (descrição absurdamente longa).
        if (y + chosen.lineHeight > maxY) break;
        ctx.fillText(line, STORY_PAD_X, y);
        y += chosen.lineHeight;
      }
      if (y + chosen.lineHeight > maxY) break;
      if (i < chosen.perPara.length - 1) y += 12;
    }
  }

  // Rodapé com nome da loja
  if (storeName) {
    const footerY = STORY_H - 90;
    ctx.strokeStyle = "#EEEEEE";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(STORY_PAD_X, footerY - 22);
    ctx.lineTo(STORY_W - STORY_PAD_X, footerY - 22);
    ctx.stroke();
    ctx.font = `400 26px ${STORY_FONT_STACK}`;
    ctx.fillStyle = "#888888";
    ctx.fillText(storeName, STORY_PAD_X, footerY);
  }

  return canvas;
}

const blank = {
  name: "", price: 0, promo_price: null, stock: 0, category_id: null,
  cost: 0, code: "", codes: [], supplier_id: null, supplier_purchase_date: null, show_in_catalog: true, allow_oversell: false,
  photos: [], variants: [], variants_label: "Variação", description: "", featured: false,
  supplier_note: "", catalog_subtitle_ids: [],
};

// Galeria com swipe nativo (mesmo padrão do PublicCatalog)
function PhotoGallery({ photos, onActiveChange }) {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const onScroll = () => {
    if (!ref.current) return;
    const w = ref.current.clientWidth;
    const idx = Math.round(ref.current.scrollLeft / w);
    setActive(idx);
    onActiveChange?.(idx);
  };
  const goTo = (i) => {
    if (!ref.current) return;
    const w = ref.current.clientWidth;
    ref.current.scrollTo({ left: i * w, behavior: "smooth" });
  };
  if (!photos || photos.length === 0) {
    return <div className="aspect-square bg-neutral-100 flex items-center justify-center text-neutral-300 text-xs">sem foto</div>;
  }
  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={onScroll}
        className="aspect-square overflow-x-auto flex snap-x snap-mandatory scrollbar-hide bg-neutral-50"
        style={{ scrollbarWidth: "none" }}
        data-testid="preview-photo-gallery"
      >
        {photos.map((src, i) => (
          <img key={i} src={src} alt="" className="w-full h-full object-cover flex-shrink-0 snap-center" style={{ width: "100%" }}/>
        ))}
      </div>
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={()=>goTo(Math.max(0, active - 1))}
            disabled={active === 0}
            className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur shadow-sm items-center justify-center text-neutral-700 hover:bg-white disabled:opacity-30"
            aria-label="Foto anterior"
          >
            <ChevronLeft size={16}/>
          </button>
          <button
            type="button"
            onClick={()=>goTo(Math.min(photos.length - 1, active + 1))}
            disabled={active === photos.length - 1}
            className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur shadow-sm items-center justify-center text-neutral-700 hover:bg-white disabled:opacity-30"
            aria-label="Próxima foto"
          >
            <ChevronRight size={16}/>
          </button>
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
            {photos.map((_, i) => (
              <span key={i}
                className="h-1.5 rounded-full transition-all"
                style={{
                  background: active === i ? "#0A0A0A" : "rgba(255,255,255,0.85)",
                  width: active === i ? 16 : 6,
                  boxShadow: active === i ? "none" : "0 0 0 1px rgba(0,0,0,0.1)",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ProductPreview({ product, categoryName, storeName, storeSlug, onClose, onEdit }) {
  // Galeria: fotos do produto + fotos das variações (sem duplicar)
  const photos = useMemo(() => {
    const arr = [];
    (product.photos || []).forEach(p => { if (p && !arr.includes(p)) arr.push(p); });
    (product.variants || []).forEach(v => { if (v.photo && !arr.includes(v.photo)) arr.push(v.photo); });
    return arr;
  }, [product]);

  const hasVariants = (product.variants || []).length > 0;
  const totalStock = hasVariants
    ? product.variants.reduce((s, v) => s + Math.max(0, Number(v.quantity || 0)), 0)
    : Math.max(0, Number(product.stock || 0));

  const [activePhoto, setActivePhoto] = useState(0);
  const [printing, setPrinting] = useState(false);
  const [sharing, setSharing] = useState(false);
  // Diálogo de seleção quando produto tem variantes (Print ou Share)
  // mode: "print" | "share"
  const [variantPickFor, setVariantPickFor] = useState(null);
  const [pickVariantName, setPickVariantName] = useState(null);
  const [pickPhotoSrc, setPickPhotoSrc] = useState(null);

  const displayPrice = product.promo_price || product.price;
  const hasPromo = !!product.promo_price;

  // Gera o blob JPEG 1080×1920 do produto (helper compartilhado entre Print e Share)
  // opts.photoSrc — sobrescreve a foto principal (usado quando seleciona variante)
  // opts.variantName — adiciona nome da variação ao título do produto
  // opts.priceOverride — usa preço da variante em vez do preço do produto
  const generateProductImageBlob = async (opts = {}) => {
    const printPhoto = opts.photoSrc || photos[activePhoto] || photos[0] || null;
    if (!printPhoto) return null;
    try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch {}
    const fullName = opts.variantName ? `${product.name} (${opts.variantName})` : product.name;
    const finalPrice = opts.priceOverride != null ? opts.priceOverride : displayPrice;
    const canvas = await renderProductStoryCanvas({
      photoSrc: printPhoto,
      productName: fullName,
      categoryName: categoryName || "",
      storeName: storeName || "",
      price: finalPrice,
      originalPrice: hasPromo && opts.priceOverride == null ? product.price : null,
      description: (product.description || "").trim(),
    });
    return await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.92));
  };

  const handlePrint = async (opts = {}) => {
    const printPhoto = opts.photoSrc || photos[activePhoto] || photos[0] || null;
    if (!printPhoto) {
      toast.error("Adicione uma foto ao produto");
      return;
    }
    // Se tem variantes e não foi explicitamente chamado com opts (ex: do diálogo),
    // abre o seletor de variante para o usuário escolher antes de gerar.
    // PADRÃO: "Sem variação" (variantName=null) — usa nome/preço base do produto.
    if (hasVariants && !opts.fromPicker) {
      setPickVariantName(null);
      setPickPhotoSrc(photos[activePhoto] || photos[0] || null);
      setVariantPickFor("print");
      return;
    }
    setPrinting(true);
    try {
      const blob = await generateProductImageBlob(opts);
      if (!blob) { toast.error("Falha ao gerar imagem"); return; }
      const safeBase = (product.name || "produto").replace(/[^\w\sÀ-ÿ-]/g, "").trim().replace(/\s+/g, "_").slice(0, 60);
      const variantSuffix = opts.variantName ? "_" + opts.variantName.replace(/[^\w\sÀ-ÿ-]/g, "").trim().replace(/\s+/g, "_") : "";
      const filename = `${safeBase || "produto"}${variantSuffix}.jpg`;

      // Detecta touch device — em desktop sempre baixa direto
      const isTouchDevice = window.matchMedia && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
      const file = new File([blob], filename, { type: "image/jpeg" });

      if (isTouchDevice && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: product.name });
          return;
        } catch (e) {
          if (e?.name === "AbortError") return;
        }
      }
      // Desktop / fallback: download direto
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      toast.success("Imagem salva");
    } catch (err) {
      toast.error("Não foi possível gerar a imagem");
    } finally {
      setPrinting(false);
    }
  };

  const handleShare = async (opts = {}) => {
    const printPhoto = opts.photoSrc || photos[activePhoto] || photos[0] || null;
    if (!printPhoto) {
      toast.error("Adicione uma foto ao produto");
      return;
    }
    if (!storeSlug) {
      toast.error("Configure o link público do catálogo em Dados da Loja antes de compartilhar.");
      return;
    }
    // Se tem variantes e não foi explicitamente chamado com opts, abre seletor
    // PADRÃO: "Sem variação" (variantName=null) — usa nome/preço base do produto.
    if (hasVariants && !opts.fromPicker) {
      setPickVariantName(null);
      setPickPhotoSrc(photos[activePhoto] || photos[0] || null);
      setVariantPickFor("share");
      return;
    }
    setSharing(true);
    try {
      const blob = await generateProductImageBlob(opts);
      if (!blob) { toast.error("Falha ao gerar imagem"); return; }
      const safeBase = (product.name || "produto").replace(/[^\w\sÀ-ÿ-]/g, "").trim().replace(/\s+/g, "_").slice(0, 60);
      const variantSuffix = opts.variantName ? "_" + opts.variantName.replace(/[^\w\sÀ-ÿ-]/g, "").trim().replace(/\s+/g, "_") : "";
      const filename = `${safeBase || "produto"}${variantSuffix}.jpg`;
      const file = new File([blob], filename, { type: "image/jpeg" });
      // Link público do produto: leva ao catálogo já com o produto aberto.
      // Ao fechar o modal no catálogo, o ?product= é removido e o usuário cai
      // na home do catálogo (lógica adicionada no PublicCatalog.jsx).
      const baseUrl = `${window.location.origin}/catalogo/${storeSlug}`;
      const productUrl = `${baseUrl}?product=${encodeURIComponent(product.id)}`;
      const text = `Veja mais informações sobre o produto aqui: ${productUrl}`;

      // Mobile: Web Share API (compartilha imagem + texto + URL juntos)
      const canShareFiles = navigator.canShare && navigator.canShare({ files: [file] });
      if (canShareFiles) {
        try {
          await navigator.share({ files: [file], title: product.name, text });
          return;
        } catch (e) {
          if (e?.name === "AbortError") return;
        }
      }
      // Fallback (desktop ou navegador sem suporte a files): copia o link
      // (ou a mensagem completa) para a área de transferência E baixa a imagem.
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Link copiado. A imagem foi baixada — anexe ao compartilhar.");
      } catch {
        toast.success("Imagem baixada. Copie o link manualmente.");
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      toast.error("Não foi possível compartilhar");
    } finally {
      setSharing(false);
    }
  };

  const printPhoto = photos[activePhoto] || photos[0] || null;
  const shortDescription = (product.description || "").trim();

  return (
    <Dialog open={true} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto p-0" data-testid="product-preview-dialog">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-20 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm"
          data-testid="preview-close-btn"
        >
          <X size={16}/>
        </button>

        <PhotoGallery photos={photos} onActiveChange={setActivePhoto} />

        <div className="p-6 space-y-4">
          <div>
            {categoryName && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">{categoryName}</div>
            )}
            <h2 className="font-display text-2xl tracking-tight" data-testid="preview-name">{product.name}</h2>
            <div className="mt-2">
              {hasPromo ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-sm line-through text-neutral-400">{brl(product.price)}</span>
                  <span className="font-display text-3xl text-emerald-700" data-testid="preview-price">{brl(product.promo_price)}</span>
                </div>
              ) : (
                <span className="font-display text-3xl" data-testid="preview-price">{brl(product.price)}</span>
              )}
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              {totalStock > 0 ? `${totalStock} em estoque` : (product.allow_oversell ? "Venda sem estoque liberada" : "Esgotado")}
            </div>
          </div>

          {hasVariants && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">{product.variants_label || "Variações"}</div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map(v => {
                  const out = !product.allow_oversell && Number(v.quantity || 0) <= 0;
                  return (
                    <span key={v.name}
                      className={`text-xs px-2 py-1 rounded border ${out ? "bg-red-50 text-red-600 border-red-200 line-through" : "bg-neutral-100 border-transparent text-neutral-700"}`}
                      title={out ? "Esgotada" : `Estoque: ${v.quantity}`}
                    >
                      {v.name} ({v.quantity || 0})
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {shortDescription && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Descrição</div>
              <p className="text-sm text-neutral-700 whitespace-pre-line leading-relaxed" data-testid="preview-description">
                {shortDescription}
              </p>
            </div>
          )}

          <div className="pt-2 grid grid-cols-3 gap-2">
            <Button
              onClick={() => handlePrint()}
              disabled={printing || !printPhoto}
              variant="outline"
              className="border-neutral-900"
              data-testid="preview-print-btn"
              title={printPhoto ? "Gerar imagem 1080×1920 para Stories / WhatsApp" : "Adicione uma foto para gerar a imagem"}
            >
              <ImageIcon size={14} className="mr-1.5"/> {printing ? "Gerando…" : "Print"}
            </Button>
            <Button
              onClick={() => handleShare()}
              disabled={sharing || !printPhoto}
              variant="outline"
              className="border-emerald-700 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
              data-testid="preview-share-link-btn"
              title={printPhoto ? "Compartilhar imagem + link público do produto" : "Adicione uma foto para compartilhar"}
            >
              <Share2 size={14} className="mr-1.5"/> {sharing ? "…" : "Compartilhar"}
            </Button>
            <Button
              onClick={onEdit}
              className="bg-[#0A0A0A] hover:bg-black text-white"
              data-testid="preview-edit-btn"
            >
              <Edit size={14} className="mr-1.5"/> Editar
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Diálogo de seleção de variante + foto para Print/Compartilhar */}
      <PrintVariantPickerDialog
        open={!!variantPickFor}
        mode={variantPickFor}
        product={product}
        photos={photos}
        variantName={pickVariantName}
        photoSrc={pickPhotoSrc}
        onVariantChange={(name) => {
          setPickVariantName(name);
          if (name == null) {
            // "Sem variação" → volta para a foto principal selecionada
            setPickPhotoSrc(photos[activePhoto] || photos[0] || null);
            return;
          }
          const v = product.variants.find(x => x.name === name);
          // Quando muda variante, padrão é a foto da variante (se houver)
          if (v?.photo) setPickPhotoSrc(v.photo);
        }}
        onPhotoChange={setPickPhotoSrc}
        onClose={() => setVariantPickFor(null)}
        onConfirm={() => {
          const variant = product.variants.find(x => x.name === pickVariantName);
          const opts = {
            fromPicker: true,
            variantName: pickVariantName || undefined,
            photoSrc: pickPhotoSrc || undefined,
            priceOverride: variant?.price,
          };
          const mode = variantPickFor;
          setVariantPickFor(null);
          if (mode === "print") handlePrint(opts);
          else handleShare(opts);
        }}
      />
    </Dialog>
  );
}

// ─── Sub-diálogo: escolha de variante + foto para Print/Compartilhar ────────
// Aparece antes de gerar a imagem 1080×1920 quando o produto tem variantes.
function PrintVariantPickerDialog({ open, mode, product, photos, variantName, photoSrc, onVariantChange, onPhotoChange, onClose, onConfirm }) {
  if (!open || !product) return null;
  const variants = product.variants || [];
  const selectedVariant = variants.find(v => v.name === variantName);
  // Fontes de foto disponíveis: foto da variante selecionada + fotos principais
  // do produto. Renderiza como uma faixa de thumbnails (variant primeiro, destacada).
  const photoSources = [];
  if (selectedVariant?.photo) {
    photoSources.push({ src: selectedVariant.photo, label: `Variação: ${selectedVariant.name}`, isVariant: true });
  }
  photos.forEach((p, i) => {
    if (p && !photoSources.find(x => x.src === p)) {
      photoSources.push({ src: p, label: `Foto ${i + 1}`, isVariant: false });
    }
  });
  const title = mode === "print" ? "Gerar imagem do produto" : "Compartilhar produto";

  return (
    <Dialog open={open} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="print-variant-picker">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-500">O preço e o nome no card final usam a variação selecionada.</p>

        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Variação</Label>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {/* Opção "Sem variação" — usa nome/preço base do produto */}
            <button
              type="button"
              onClick={() => onVariantChange(null)}
              className={`text-xs px-3 py-1.5 rounded-md border transition ${variantName == null ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]" : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10 dark:text-neutral-300"}`}
              data-testid="print-var-none"
            >
              Sem variação
            </button>
            {variants.map(v => (
              <button
                key={v.name}
                type="button"
                onClick={() => onVariantChange(v.name)}
                className={`text-xs px-3 py-1.5 rounded-md border transition ${variantName === v.name ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]" : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10 dark:text-neutral-300"}`}
                data-testid={`print-var-${v.name}`}
              >
                {v.name}{v.price != null ? <span className="ml-1.5 text-neutral-400 dark:text-neutral-500">{brl(v.price)}</span> : null}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Foto a usar</Label>
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
            {photoSources.map((src, i) => {
              const active = photoSrc === src.src;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onPhotoChange(src.src)}
                  className={`relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition ${active ? "border-emerald-500" : "border-transparent hover:border-neutral-300"}`}
                  title={src.label}
                  data-testid={`print-photo-${i}`}
                >
                  <img src={src.src} alt={src.label} className="w-full h-full object-cover"/>
                  {src.isVariant && <div className="absolute bottom-0 inset-x-0 bg-emerald-500 text-white text-[9px] py-0.5 text-center font-medium">Variação</div>}
                </button>
              );
            })}
            {photoSources.length === 0 && (
              <div className="text-xs text-neutral-400">Sem fotos disponíveis</div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={onConfirm}
            disabled={!photoSrc}
            className="bg-[#0A0A0A] hover:bg-black text-white"
            data-testid="print-variant-confirm"
          >
            {mode === "print" ? "Gerar imagem" : "Compartilhar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Produtos() {
  const { canSeeFinance, canManageProducts, user } = useAuth();
  const storeSlug = user?.tenant?.slug || user?.impersonated_tenant?.slug || "";
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [previewing, setPreviewing] = useState(null); // produto FULL para visualização
  const [storeName, setStoreName] = useState("");
  const [newCat, setNewCat] = useState("");
  const [newSup, setNewSup] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [codeDraft, setCodeDraft] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [subtitlesMap, setSubtitlesMap] = useState({}); // id → {text,bg_color}

  const load = async () => {
    // Cada chamada é independente — colaborador SEM permissão recebe [] em
    // /suppliers e /categories pode até falhar; nada disso pode quebrar a tela.
    const safe = (p) => p.catch(() => ({ data: [] }));
    const [p, c, s, sub] = await Promise.all([
      api.get("/products", { params: { q: q || undefined, light: true } }),
      safe(api.get("/categories")),
      safe(api.get("/suppliers")),
      safe(api.get("/catalog-subtitles")),
    ]);
    setProducts(p.data); setCats(c.data || []); setSuppliers(s.data || []);
    setSubtitlesMap(Object.fromEntries((sub.data || []).map(x => [x.id, x])));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line
  useEffect(() => { api.get("/store").then(r => setStoreName(r.data?.name || "")).catch(()=>{}); }, []);

  const catsMap = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c])), [cats]);

  // Aplica filtro por categoria + ordena com destaques no topo
  const visibleProducts = useMemo(() => {
    let list = products;
    if (categoryFilter !== "all") {
      list = list.filter(p => (p.category_id || "none") === categoryFilter);
    }
    return sortProducts(list, catsMap);
  }, [products, categoryFilter, catsMap]);

  // Stats do estoque (somente quem vê finanças)
  const stockStats = useMemo(() => {
    let cost = 0, value = 0;
    products.forEach(p => {
      const variants = p.variants || [];
      if (variants.length > 0) {
        variants.forEach(v => {
          const qty = Math.max(0, Number(v.quantity || 0));
          if (qty <= 0) return;
          cost += qty * Number(v.cost || 0);
          value += qty * Number(v.price || 0);
        });
      } else {
        const qty = Math.max(0, Number(p.stock || 0));
        if (qty <= 0) return;
        cost += qty * Number(p.cost || 0);
        value += qty * Number(p.promo_price || p.price || 0);
      }
    });
    return { cost, value, profit: value - cost };
  }, [products]);

  const toggleFeatured = async (p, e) => {
    e?.stopPropagation?.();
    // Atualização otimista — UI muda instantâneo
    const newValue = !p.featured;
    setProducts(prev => prev.map(x => x.id === p.id ? { ...x, featured: newValue } : x));
    try {
      // Busca o produto FULL e atualiza apenas o featured (preserva fotos/descrição)
      const r = await api.get(`/products/${p.id}`);
      const full = r.data;
      await api.put(`/products/${p.id}`, { ...full, featured: newValue });
      toast.success(newValue ? "Produto destacado" : "Destaque removido");
      load(); // sincroniza
    } catch (err) {
      // Reverte em caso de erro
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, featured: !newValue } : x));
      toast.error("Erro ao atualizar destaque");
    }
  };

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const addCode = (raw) => {
    const c = String(raw || "").trim();
    if (!c) return;
    setForm(p => {
      const list = Array.isArray(p.codes) ? p.codes : [];
      if (list.includes(c)) {
        toast.info("Esse código já está cadastrado");
        return p;
      }
      return { ...p, codes: [...list, c] };
    });
  };
  const removeCode = (idx) => setForm(p => ({ ...p, codes: (p.codes || []).filter((_, i) => i !== idx) }));
  const editCode = (idx, val) => setForm(p => ({
    ...p,
    codes: (p.codes || []).map((c, i) => i === idx ? val : c),
  }));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const arr = await Promise.all(files.map(fileToBase64));
    update("photos", [...form.photos, ...arr]);
  };

  const addVariant = () => update("variants", [...form.variants, { name: "", quantity: 0, cost: form.cost, price: form.price, photo: "" }]);
  const updVariant = (i, k, v) => update("variants", form.variants.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmVariant = (i) => update("variants", form.variants.filter((_, idx) => idx !== i));

  const handleVariantPhoto = async (i, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await fileToBase64(f);
    updVariant(i, "photo", b64);
  };

  const variantsTotal = form.variants.reduce((s, v) => s + Number(v.quantity || 0), 0);

  const openNew = () => { setForm(blank); setEditingId(null); setOpen(true); };
  const openEdit = async (p) => {
    // Busca o produto FULL (com photos/description) — a listagem usa ?light=true
    let full = p;
    try {
      const r = await api.get(`/products/${p.id}`);
      full = r.data || p;
    } catch { /* fallback para o objeto da lista */ }
    // Normaliza: codes (lista) tem prioridade; se vier vazio mas houver code legado, usa-o
    let codes = Array.isArray(full.codes) ? full.codes.filter(Boolean) : [];
    if (codes.length === 0 && full.code) codes = [full.code];
    setForm({ ...blank, ...full, codes, photos: full.photos || [], variants: full.variants || [] });
    setEditingId(p.id); setOpen(true);
  };

  const openPreview = async (p) => {
    // Listagem usa ?light=true (sem photos/description). Busca o produto completo.
    try {
      const r = await api.get(`/products/${p.id}`);
      setPreviewing(r.data || p);
    } catch {
      setPreviewing(p);
    }
  };

  const previewToEdit = () => {
    const p = previewing;
    setPreviewing(null);
    if (p) openEdit(p);
  };

  const submit = async () => {
    try {
      const payload = {
        ...form,
        price: Number(form.price), cost: Number(form.cost), stock: Number(form.stock),
        promo_price: form.promo_price === "" || form.promo_price === null ? null : Number(form.promo_price),
        variants: form.variants.map(v => ({ ...v, quantity: Number(v.quantity), cost: Number(v.cost), price: Number(v.price) })),
      };
      if (editingId) await api.put(`/products/${editingId}`, payload);
      else await api.post("/products", payload);
      setOpen(false); toast.success("Produto salvo"); load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 413) {
        toast.error("As imagens estão grandes demais. Tente fotos menores.");
      } else if (detail) {
        toast.error(typeof detail === "string" ? detail : "Erro ao salvar");
      } else {
        toast.error("Erro ao salvar — verifique sua conexão e tente novamente.");
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir produto?")) return;
    await api.delete(`/products/${id}`); toast.success("Excluído"); load();
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    await api.post("/categories", { name: newCat.trim() });
    setNewCat(""); load();
  };
  const removeCategory = async (id) => { await api.delete(`/categories/${id}`); load(); };
  const addSupplier = async () => {
    if (!newSup.trim()) return;
    await api.post("/suppliers", { name: newSup.trim() });
    setNewSup(""); load();
  };
  const removeSupplier = async (id) => { await api.delete(`/suppliers/${id}`); load(); };

  // ─── Expert IA — gera descrição automática a partir do nome do produto ───
  const [aiBusy, setAiBusy] = useState(false);
  const aiGenerateDescription = async () => {
    const name = (form.name || "").trim();
    if (!name) {
      toast.error("Informe o nome do produto antes de usar a IA");
      return;
    }
    if (form.description && !window.confirm("Já existe uma descrição. Substituir pelo texto gerado pela IA?")) {
      return;
    }
    setAiBusy(true);
    let toastId;
    try {
      // Toast persistente enquanto a IA pensa — usuário sabe que algo está acontecendo
      toastId = toast.loading("Expert IA escrevendo a descrição…", { duration: 60000 });
      const cat = catsMap[form.category_id]?.name || "";
      const r = await api.post("/products/ai-description", {
        product_name: name,
        category_name: cat || undefined,
      }, { timeout: 60000 });
      const desc = (r.data?.description || "").trim();
      if (!desc) {
        toast.dismiss(toastId);
        toast.error("A IA não retornou descrição. Tente novamente.");
        return;
      }
      update("description", desc);
      toast.dismiss(toastId);
      toast.success("Descrição gerada pela Expert IA");
    } catch (e) {
      if (toastId) toast.dismiss(toastId);
      const detail = e?.response?.data?.detail;
      // Log estruturado para debug
      console.error("[AI description] falhou:", {
        status: e?.response?.status,
        detail,
        message: e?.message,
      });
      toast.error(typeof detail === "string" ? detail : "Erro ao gerar descrição — tente novamente em alguns segundos");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="produtos-page">
      <div className="flex flex-wrap gap-4 items-end justify-between">
        <div>
          <p className="label-tiny mb-2">Estoque</p>
          <h1 className="font-display text-4xl tracking-tight">Produtos</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canSeeFinance && (
            <Button variant="outline" onClick={()=>setExportOpen(true)} data-testid="btn-export-produtos">
              <Download size={14} className="mr-1"/> Exportar relatório
            </Button>
          )}
          {canManageProducts && (
            <Button onClick={openNew} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-product-button">
              <Plus size={16} className="mr-1"/> Novo produto
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list">Estoque</TabsTrigger>
          {canManageProducts && <TabsTrigger value="cats">Categorias</TabsTrigger>}
          {canSeeFinance && <TabsTrigger value="sups">Fornecedores</TabsTrigger>}
        </TabsList>

        <TabsContent value="list" className="mt-6">
          {canSeeFinance && (
            <Card className="p-4 border-[#E5E5E5] shadow-none mb-4" data-testid="stock-stats-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs uppercase tracking-[0.2em] text-neutral-500">Resumo do estoque (apenas itens com qtd. disponível)</div>
                <div className="flex items-center gap-6 flex-wrap text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Custo do estoque</div>
                    <div className="font-display text-base mt-0.5" data-testid="stock-cost"><Money value={stockStats.cost}/></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Valor do estoque</div>
                    <div className="font-display text-base mt-0.5" data-testid="stock-value"><Money value={stockStats.value}/></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Lucro previsto</div>
                    <div className="font-display text-base mt-0.5 text-emerald-700" data-testid="stock-profit"><Money value={stockStats.profit}/></div>
                  </div>
                  <HideMoneyButton testId="produtos-hide-money"/>
                </div>
              </div>
            </Card>
          )}

          <div className="flex flex-wrap gap-3 mb-4 items-end">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar por nome…" className="pl-9" data-testid="product-search-input"/>
            </div>
            <div className="min-w-[180px]">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="product-category-filter"><SelectValue placeholder="Categoria"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  <SelectItem value="none">Sem categoria</SelectItem>
                  {cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {visibleProducts.map(p => (
              <Card key={p.id} className={`relative border-[#E5E5E5] shadow-none hover:-translate-y-1 transition ${p.featured ? "ring-1 ring-amber-300" : ""}`} data-testid={`product-card-${p.id}`}>
                {p.featured && (
                  <div className="absolute top-2 left-2 z-10 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm" data-testid={`featured-badge-${p.id}`}>
                    <Star size={10} fill="currentColor"/> Destaque
                  </div>
                )}
                <button
                  onClick={(e)=>toggleFeatured(p, e)}
                  className={`absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center transition shadow-sm ${p.featured ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-white/90 backdrop-blur text-neutral-400 hover:text-amber-500"}`}
                  title={p.featured ? "Remover destaque" : "Destacar produto"}
                  data-testid={`toggle-featured-${p.id}`}
                >
                  <Star size={13} fill={p.featured ? "currentColor" : "none"}/>
                </button>
                <button
                  type="button"
                  onClick={()=>openPreview(p)}
                  className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900"
                  data-testid={`preview-product-${p.id}`}
                  title="Visualizar produto"
                >
                  <div className="aspect-square bg-neutral-100 overflow-hidden">
                    {p.photos?.[0] ? <img src={p.photos[0]} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform group-hover:scale-105"/> : <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs">sem foto</div>}
                  </div>
                  <div className="px-4 pt-4">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    {(p.catalog_subtitle_ids || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`product-card-subtitles-${p.id}`}>
                        {p.catalog_subtitle_ids.map(sid => {
                          const s = subtitlesMap[sid];
                          if (!s) return null;
                          const bg = s.bg_color || "#FEF3C7";
                          return (
                            <span
                              key={sid}
                              className="text-[9px] font-medium px-1.5 py-0.5 rounded-full"
                              style={{ background: bg, color: readableTextColor(bg) }}
                            >
                              {s.text}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        {p.promo_price ? (
                          <>
                            <div className="text-xs line-through text-neutral-400">{brl(p.price)}</div>
                            <div className="text-sm font-display">{brl(p.promo_price)}</div>
                          </>
                        ) : (
                          <div className="text-sm font-display">{brl(p.price)}</div>
                        )}
                      </div>
                      <div className="text-xs text-neutral-500">est: {p.stock}</div>
                    </div>
                  </div>
                </button>
                {canManageProducts && (
                  <div className="px-4 pb-4 pt-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="flex-1" onClick={()=>openEdit(p)} data-testid={`edit-product-${p.id}`}><Edit size={12}/></Button>
                      <Button size="sm" variant="outline" onClick={()=>remove(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 size={12}/></Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
            {visibleProducts.length === 0 && <div className="col-span-full text-sm text-neutral-500 py-12 text-center">Nenhum produto {categoryFilter !== "all" ? "nesta categoria" : "cadastrado"}.</div>}
          </div>
        </TabsContent>

        <TabsContent value="cats" className="mt-6">
          <Card className="p-6 border-[#E5E5E5] shadow-none max-w-xl">
            <div className="flex gap-2">
              <Input value={newCat} onChange={(e)=>setNewCat(e.target.value)} placeholder="Nova categoria" data-testid="new-category-input"/>
              <Button onClick={addCategory} data-testid="add-category-button"><Plus size={14}/></Button>
            </div>
            <ul className="mt-4 divide-y divide-[#E5E5E5]">
              {cats.map(c => (
                <li key={c.id} className="flex items-center justify-between py-2 text-sm">
                  <span>{c.name}</span>
                  <Button size="sm" variant="ghost" onClick={()=>removeCategory(c.id)}><Trash2 size={12}/></Button>
                </li>
              ))}
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="sups" className="mt-6">
          <Card className="p-6 border-[#E5E5E5] shadow-none max-w-xl">
            <div className="flex gap-2">
              <Input value={newSup} onChange={(e)=>setNewSup(e.target.value)} placeholder="Novo fornecedor" data-testid="new-supplier-input"/>
              <Button onClick={addSupplier}><Plus size={14}/></Button>
            </div>
            <ul className="mt-4 divide-y divide-[#E5E5E5]">
              {suppliers.map(s => (
                <li key={s.id} className="flex items-center justify-between py-2 text-sm">
                  <span>{s.name}</span>
                  <Button size="sm" variant="ghost" onClick={()=>removeSupplier(s.id)}><Trash2 size={12}/></Button>
                </li>
              ))}
            </ul>
          </Card>
        </TabsContent>
      </Tabs>

      {previewing && (
        <ProductPreview
          product={previewing}
          categoryName={catsMap[previewing.category_id]?.name}
          storeName={storeName}
          storeSlug={storeSlug}
          onClose={()=>setPreviewing(null)}
          onEdit={previewToEdit}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          <Tabs defaultValue="main">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="main">Principal</TabsTrigger>
              <TabsTrigger value="details">Detalhes</TabsTrigger>
              <TabsTrigger value="variants">Variações</TabsTrigger>
            </TabsList>

            <TabsContent value="main" className="mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label>Nome</Label><Input value={form.name} onChange={e=>update("name", e.target.value)} data-testid="product-name-input"/></div>
                <div><Label>Valor</Label><CurrencyInput value={form.price} onChange={(v)=>update("price", v)} data-testid="product-price-input"/></div>
                <div><Label>Valor promocional</Label><CurrencyInput value={form.promo_price ?? 0} onChange={(v)=>update("promo_price", v || null)} data-testid="product-promo-input"/></div>
                <div><Label>Custo</Label><CurrencyInput value={form.cost} onChange={(v)=>update("cost", v)} data-testid="product-cost-input"/></div>
                <div>
                  <Label>Quantidade em estoque{form.variants.length > 0 && <span className="text-xs text-neutral-400 ml-1">(automático)</span>}</Label>
                  <Input
                    type="number"
                    value={form.variants.length > 0 ? variantsTotal : form.stock}
                    onChange={e=>update("stock", e.target.value)}
                    disabled={form.variants.length > 0}
                    data-testid="product-stock-input"
                  />
                  {form.variants.length > 0 && <p className="text-xs text-neutral-500 mt-1">Soma das variações.</p>}
                  <div className="flex items-center gap-2 mt-2">
                    <Checkbox id="allow_over" checked={form.allow_oversell} onCheckedChange={(v)=>update("allow_oversell", !!v)} data-testid="product-allow-oversell-checkbox"/>
                    <Label htmlFor="allow_over" className="text-xs cursor-pointer">Vender sem estoque</Label>
                  </div>
                </div>
                <div className="md:col-span-2 flex items-center justify-between border border-amber-200 bg-amber-50/50 rounded-md px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Star size={14} className="text-amber-500" fill="currentColor"/>
                    <Label htmlFor="featured" className="cursor-pointer text-sm">Destacar produto</Label>
                  </div>
                  <Switch id="featured" checked={!!form.featured} onCheckedChange={(v)=>update("featured", !!v)} data-testid="product-featured-switch"/>
                </div>
                <div className="md:col-span-2 -mt-2">
                  <p className="text-[11px] text-neutral-500">Produtos destacados aparecem no topo do estoque, das vendas e do catálogo público.</p>
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <Checkbox id="show_cat" checked={form.show_in_catalog} onCheckedChange={(v)=>update("show_in_catalog", !!v)} data-testid="product-show-catalog-checkbox"/>
                  <Label htmlFor="show_cat" className="cursor-pointer">Mostrar no site (catálogo público)</Label>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-neutral-600">
                    Subtítulo do catálogo
                    <span className="text-neutral-400 font-normal ml-1">(badges no card do produto — só catálogo online)</span>
                  </Label>
                  <SubtitleSelector
                    value={form.catalog_subtitle_ids || []}
                    onChange={(ids) => update("catalog_subtitle_ids", ids)}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Fotos</Label>
                  <Input type="file" accept="image/*" multiple onChange={handlePhotos} data-testid="product-photos-input"/>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {form.photos.map((p, i) => (
                      <div key={i} className="relative">
                        <img src={p} alt="" className="w-16 h-16 object-cover rounded border border-[#E5E5E5]"/>
                        <button onClick={()=>update("photos", form.photos.filter((_,idx)=>idx!==i))} className="absolute -top-2 -right-2 bg-white border border-[#E5E5E5] rounded-full p-0.5"><X size={10}/></button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="details" className="mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Códigos de barras / QR</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={codeDraft}
                      onChange={e=>setCodeDraft(e.target.value)}
                      onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); addCode(codeDraft); setCodeDraft(""); }}}
                      placeholder="Digite o código e Enter, ou use o leitor →"
                      data-testid="product-code-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={()=>{ if (codeDraft.trim()) { addCode(codeDraft); setCodeDraft(""); } }}
                      title="Adicionar código digitado"
                      data-testid="product-code-add"
                    >
                      <Plus size={14}/>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={()=>setScanOpen(true)}
                      title="Ler código com a câmera"
                      data-testid="product-code-scan"
                    >
                      <ScanLine size={14} className="mr-1"/> Ler código
                    </Button>
                  </div>
                  {(form.codes || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(form.codes || []).map((c, idx) => (
                        <div key={idx} className="inline-flex items-center gap-1 bg-neutral-100 border border-[#E5E5E5] rounded-md pl-2 pr-1 py-1 text-xs">
                          <input
                            value={c}
                            onChange={(e)=>editCode(idx, e.target.value)}
                            className="bg-transparent border-0 outline-0 text-xs font-mono min-w-[70px] max-w-[180px]"
                            style={{ width: `${Math.max(70, (c?.length || 0) * 8)}px` }}
                            data-testid={`product-code-item-${idx}`}
                          />
                          <button
                            type="button"
                            onClick={()=>removeCode(idx)}
                            className="ml-0.5 text-neutral-400 hover:text-red-600"
                            title="Remover este código"
                            data-testid={`product-code-remove-${idx}`}
                          ><X size={12}/></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-neutral-500 mt-1">Você pode adicionar quantos códigos quiser pro mesmo produto (várias embalagens, EAN-13, EAN-8, QR).</p>
                </div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category_id || "none"} onValueChange={(v)=>update("category_id", v === "none" ? null : v)}>
                    <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fornecedor</Label>
                  <Select value={form.supplier_id || "none"} onValueChange={(v)=>{
                    const newSup = v === "none" ? null : v;
                    update("supplier_id", newSup);
                    // Ao selecionar fornecedor, pré-preenche data com HOJE
                    // (usuário depois pode escolher "Outra" para editar).
                    if (newSup && !form.supplier_purchase_date) {
                      update("supplier_purchase_date", new Date().toISOString().slice(0, 10));
                    }
                  }}>
                    <SelectTrigger data-testid="product-supplier-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.supplier_id && (
                  <PurchaseDatePicker
                    value={form.supplier_purchase_date}
                    onChange={(v) => update("supplier_purchase_date", v)}
                    testIdPrefix="product-purchase-date"
                  />
                )}
                {(() => {
                  const sup = suppliers.find(s => s.id === form.supplier_id);
                  if (sup && /troca/i.test(sup.name || "")) {
                    return (
                      <div className="md:col-span-2">
                        <Label>Observação da Troca <span className="text-[10px] text-neutral-400 font-normal">(interno)</span></Label>
                        <Input
                          value={form.supplier_note || ""}
                          onChange={e=>update("supplier_note", e.target.value)}
                          placeholder="Ex.: produto recebido na troca do cliente Martin"
                          data-testid="product-supplier-note-input"
                        />
                        <p className="text-[11px] text-neutral-500 mt-1">Visível apenas para você no painel.</p>
                      </div>
                    );
                  }
                  return null;
                })()}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                    <Label className="mb-0">Detalhes / Informações</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={aiGenerateDescription}
                      disabled={aiBusy || !form.name?.trim()}
                      className="h-8 border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800 disabled:opacity-50"
                      title={form.name?.trim() ? "Gerar descrição automática a partir do nome do produto" : "Preencha o nome do produto antes"}
                      data-testid="ai-description-button"
                    >
                      <Sparkles size={13} className={`mr-1.5 ${aiBusy ? "animate-pulse" : ""}`}/>
                      {aiBusy ? "Gerando…" : "Criar com Expert IA 🤖"}
                    </Button>
                  </div>
                  <Textarea
                    value={form.description}
                    onChange={e=>update("description", e.target.value)}
                    rows={6}
                    placeholder="Descrição completa do produto que aparecerá no catálogo público (opcional). Ex: composição, modo de uso, dicas, especificações…"
                    data-testid="product-description-input"
                  />
                  <p className="text-xs text-neutral-500 mt-1">Aparece na página do produto no catálogo público. Você pode editar o texto gerado pela IA antes de salvar.</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="variants" className="mt-4">
              <div className="flex justify-between items-center mb-3">
                <Label>Variações do produto</Label>
                <Button size="sm" variant="outline" onClick={addVariant} data-testid="add-variant-button"><Plus size={12} className="mr-1"/> Adicionar</Button>
              </div>
              {form.variants.length > 0 && (
                <div className="mb-3">
                  <Label className="text-xs">Tipo da variação (aparece como "Selecionar [tipo]" no catálogo)</Label>
                  <Input
                    value={form.variants_label}
                    onChange={e=>update("variants_label", e.target.value)}
                    placeholder="Ex: Sabor, Cor, Tamanho"
                    data-testid="variants-label-input"
                  />
                </div>
              )}
              {form.variants.map((v, i) => (
                <div key={i} className="border border-[#E5E5E5] rounded-md p-3 mb-3 space-y-2">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 sm:col-span-4"><Label className="text-xs">Nome</Label><Input value={v.name} onChange={e=>updVariant(i, "name", e.target.value)} placeholder="Ex: Vermelho M"/></div>
                    <div className="col-span-4 sm:col-span-2"><Label className="text-xs">Qtd</Label><Input type="number" value={v.quantity} onChange={e=>updVariant(i, "quantity", e.target.value)}/></div>
                    <div className="col-span-4 sm:col-span-2"><Label className="text-xs">Custo</Label><CurrencyInput value={v.cost} onChange={(val)=>updVariant(i, "cost", val)}/></div>
                    <div className="col-span-4 sm:col-span-3"><Label className="text-xs">Venda</Label><CurrencyInput value={v.price} onChange={(val)=>updVariant(i, "price", val)}/></div>
                    <Button size="sm" variant="ghost" onClick={()=>rmVariant(i)} className="col-span-12 sm:col-span-1"><Trash2 size={12}/></Button>
                  </div>
                  <div className="flex items-center gap-3">
                    {v.photo ? (
                      <div className="relative">
                        <img src={v.photo} alt="" className="w-14 h-14 object-cover rounded border border-[#E5E5E5]"/>
                        <button onClick={()=>updVariant(i, "photo", "")} className="absolute -top-2 -right-2 bg-white border border-[#E5E5E5] rounded-full p-0.5"><X size={10}/></button>
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded border border-dashed border-[#E5E5E5] flex items-center justify-center text-[10px] text-neutral-400">sem foto</div>
                    )}
                    <div className="flex-1">
                      <Label className="text-xs">Foto da variação</Label>
                      <Input type="file" accept="image/*" onChange={(e)=>handleVariantPhoto(i, e)} className="text-xs" data-testid={`variant-photo-${i}`}/>
                    </div>
                  </div>
                </div>
              ))}
              {form.variants.length === 0 && <p className="text-xs text-neutral-500">Nenhuma variação. O produto será vendido como item único.</p>}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-product-button">Salvar</Button>
          </DialogFooter>

          {/* Aninhado dentro do DialogContent para Radix manter o foco do
              dialog pai aberto quando o scanner abrir por cima. */}
          <BarcodeScanner
            open={scanOpen}
            continuous={true}
            onClose={()=>setScanOpen(false)}
            onDetect={(code)=>{ addCode(code); toast.success(`Código "${code}" adicionado`); }}
            title="Ler códigos do produto"
          />
        </DialogContent>
      </Dialog>
      <ProductsExportDialog open={exportOpen} onClose={()=>setExportOpen(false)} products={products} categories={cats} suppliers={suppliers}/>
    </div>
  );
}

/**
 * PurchaseDatePicker — seletor da data de compra ao fornecedor.
 * Modo padrão "Hoje" (usa a data atual); botão "Outra" abre input date.
 * Usado tanto no cadastro do Produto quanto no fluxo de item avulso no PDV.
 */
function PurchaseDatePicker({ value, onChange, testIdPrefix = "purchase-date" }) {
  const today = new Date().toISOString().slice(0, 10);
  const isToday = !value || value === today;
  const [mode, setMode] = React.useState(isToday ? "today" : "other");
  React.useEffect(() => {
    // Ao mudar externamente (ex.: reset do form), sincroniza o modo
    setMode(value && value !== today ? "other" : "today");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div>
      <Label>Data da compra <span className="text-[10px] text-neutral-400 font-normal">(interno)</span></Label>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        <div className="inline-flex rounded-md border border-[#E5E5E5] overflow-hidden text-sm">
          <button
            type="button"
            className={`px-3 py-1.5 ${mode === "today" ? "bg-[#0A0A0A] text-white" : "bg-white dark:bg-white/5 hover:bg-neutral-50"}`}
            onClick={() => { setMode("today"); onChange(today); }}
            data-testid={`${testIdPrefix}-today`}
          >Hoje</button>
          <button
            type="button"
            className={`px-3 py-1.5 border-l border-[#E5E5E5] ${mode === "other" ? "bg-[#0A0A0A] text-white" : "bg-white dark:bg-white/5 hover:bg-neutral-50"}`}
            onClick={() => setMode("other")}
            data-testid={`${testIdPrefix}-other`}
          >Outra</button>
        </div>
        {mode === "other" && (
          <Input
            type="date"
            value={value || today}
            onChange={(e) => onChange(e.target.value)}
            className="w-40 h-9"
            data-testid={`${testIdPrefix}-input`}
          />
        )}
        {mode === "today" && (
          <span className="text-xs text-neutral-500">{new Date(today + "T00:00:00").toLocaleDateString('pt-BR')}</span>
        )}
      </div>
    </div>
  );
}

